import { useMemo } from 'react';
import type { LanguageCode } from '@/types';

interface DateRangeSliderProps {
  lang: LanguageCode;
  /** A csúszka alsó határa — mindig a mai nap. */
  minDate: Date;
  /** A csúszka felső határa — a legkésőbbi aktív esemény dátuma. */
  maxDate: Date;
  valueFrom: Date;
  valueTo: Date;
  onChange: (from: Date, to: Date) => void;
  label: string;
}

function startOfDay(d: Date): Date {
  const copy = new Date(d);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

function daysBetween(a: Date, b: Date): number {
  return Math.round((startOfDay(b).getTime() - startOfDay(a).getTime()) / 86_400_000);
}

function addDays(d: Date, days: number): Date {
  const copy = startOfDay(d);
  copy.setDate(copy.getDate() + days);
  return copy;
}

const LOCALE_MAP: Record<LanguageCode, string> = { hu: 'hu-HU', en: 'en-GB', de: 'de-DE' };

export function DateRangeSlider({ lang, minDate, maxDate, valueFrom, valueTo, onChange, label }: DateRangeSliderProps) {
  const totalDays = Math.max(1, daysBetween(minDate, maxDate));
  const fromIdx = Math.min(Math.max(0, daysBetween(minDate, valueFrom)), totalDays);
  const toIdx = Math.min(Math.max(0, daysBetween(minDate, valueTo)), totalDays);

  const formatter = useMemo(
    () => new Intl.DateTimeFormat(LOCALE_MAP[lang] ?? 'hu-HU', { month: 'short', day: 'numeric' }),
    [lang],
  );

  const fromPct = (fromIdx / totalDays) * 100;
  const toPct = (toIdx / totalDays) * 100;

  return (
    <div className="shrink-0 px-4 kiosk:px-[3vw] pb-3 kiosk:pb-[1.5vw]">
      <div className="flex items-center justify-between mb-1.5 kiosk:mb-[0.6vw]">
        <span className="font-display text-kiosk-xs text-kiosk-muted">{label}</span>
        <span className="font-display text-kiosk-xs text-kiosk-text tabular-nums">
          {formatter.format(valueFrom)} – {formatter.format(valueTo)}
        </span>
      </div>
      <div className="relative h-7 kiosk:h-[2vw] flex items-center">
        <div className="absolute inset-x-0 h-1.5 kiosk:h-[0.4vw] rounded-full bg-kiosk-border" />
        <div
          className="absolute h-1.5 kiosk:h-[0.4vw] rounded-full bg-kiosk-accent"
          style={{ left: `${fromPct}%`, right: `${100 - toPct}%` }}
        />
        <input
          type="range"
          className="date-range-thumb"
          min={0}
          max={totalDays}
          value={fromIdx}
          onChange={(e) => {
            const idx = Math.min(Number(e.target.value), toIdx);
            onChange(addDays(minDate, idx), valueTo);
          }}
          aria-label="Kezdő dátum"
        />
        <input
          type="range"
          className="date-range-thumb"
          min={0}
          max={totalDays}
          value={toIdx}
          onChange={(e) => {
            const idx = Math.max(Number(e.target.value), fromIdx);
            onChange(valueFrom, addDays(minDate, idx));
          }}
          aria-label="Záró dátum"
        />
      </div>
    </div>
  );
}
